import { useState } from 'react';
import {
  DEFAULT_DINE_OUT_CENTS_PER_PERSON,
  type HouseholdPersonDto,
  type MealPlaceDto,
  type MealSlot,
} from '@fewerbags/schema';
import {
  dollarsInput,
  formatDollars,
  parseDollars,
  type AttendeeChoice,
  type DineOutChoice,
} from '../../lib/autoplan';
import { formatClock } from '../../lib/planner';
import { SLOT_LABEL } from '../../lib/plans';
import { WEEKDAY_LABEL } from '../../lib/schedule';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { Field } from '../Field';
import { AttendeePicker, chipButton } from './AttendeePicker';

export interface CellValue {
  /** null = follow the slot's default. */
  attendees: AttendeeChoice | null;
  dineOut: DineOutChoice | null;
  /** Lunch only; null = at home. */
  packedPlaceId: string | null;
}

/**
 * One weekday × slot of the auto-plan grid: who eats, dining out (note and
 * estimate), and for lunch where it is packed for. Edits a draft and hands it
 * back on "Done".
 */
export function AutoplanCellDialog({
  open,
  weekday,
  slot,
  value,
  slotDefault,
  usualLabel,
  serveAt,
  people,
  places,
  onClose,
  onSave,
}: {
  open: boolean;
  weekday: number;
  slot: MealSlot;
  value: CellValue;
  /** The slot's default (what `attendees: null` means). */
  slotDefault: AttendeeChoice;
  usualLabel: string;
  serveAt: string | null;
  people: readonly HouseholdPersonDto[];
  places: readonly MealPlaceDto[];
  onClose: () => void;
  onSave: (next: CellValue) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [costText, setCostText] = useState(dollarsInput(value.dineOut?.estCostCents ?? null));
  const idPrefix = `cell-${weekday}-${slot}`;
  const away = places.filter((p) => p.kind !== 'home');
  const costInvalid = costText.trim() !== '' && parseDollars(costText) === null;

  function save() {
    if (costInvalid) return;
    onSave({
      ...draft,
      dineOut: draft.dineOut ? { ...draft.dineOut, estCostCents: parseDollars(costText) } : null,
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`${WEEKDAY_LABEL[weekday]} ${SLOT_LABEL[slot].toLowerCase()}`}
    >
      {serveAt ? (
        <p className="-mt-2 text-sm text-muted">Usually served at {formatClock(serveAt)}</p>
      ) : null}

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-semibold text-ink">Who eats</legend>
        <label className="flex min-h-11 items-center gap-2 text-sm font-medium text-ink">
          <input
            type="checkbox"
            className="size-5 accent-brand"
            checked={draft.attendees === null}
            onChange={(e) =>
              setDraft((d) => ({ ...d, attendees: e.target.checked ? null : slotDefault }))
            }
          />
          Same as every {SLOT_LABEL[slot].toLowerCase()}
        </label>
        {draft.attendees !== null ? (
          <AttendeePicker
            idPrefix={idPrefix}
            choice={draft.attendees}
            people={people}
            usualLabel={usualLabel}
            onChange={(attendees) => setDraft((d) => ({ ...d, attendees }))}
          />
        ) : null}
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-semibold text-ink">Dining out</legend>
        <label className="flex min-h-11 items-center gap-2 text-sm font-medium text-ink">
          <input
            type="checkbox"
            className="size-5 accent-brand"
            checked={draft.dineOut !== null}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                dineOut: e.target.checked ? { note: '', estCostCents: null } : null,
              }))
            }
          />
          We&apos;re eating out
        </label>
        {draft.dineOut !== null ? (
          <div className="grid gap-2 sm:grid-cols-2">
            <Field
              id={`${idPrefix}-note`}
              label="Note (optional)"
              value={draft.dineOut.note}
              maxLength={200}
              placeholder="Pizza at Luigi's"
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  dineOut: { note: e.target.value, estCostCents: d.dineOut?.estCostCents ?? null },
                }))
              }
            />
            <Field
              id={`${idPrefix}-cost`}
              label="Estimated cost (optional)"
              inputMode="decimal"
              value={costText}
              placeholder={`${formatDollars(DEFAULT_DINE_OUT_CENTS_PER_PERSON)} per person`}
              onChange={(e) => setCostText(e.target.value)}
              error={costInvalid ? 'Enter an amount like 45 or 45.50' : undefined}
            />
          </div>
        ) : null}
      </fieldset>

      {slot === 'lunch' && draft.dineOut === null ? (
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-semibold text-ink">Packed for</legend>
          {away.length === 0 ? (
            <p className="text-sm text-muted">
              No work or school places yet — packed lunches need one.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2" role="group" aria-label="Packed for">
              <button
                type="button"
                className={chipButton(draft.packedPlaceId === null)}
                aria-pressed={draft.packedPlaceId === null}
                onClick={() => setDraft((d) => ({ ...d, packedPlaceId: null }))}
              >
                At home
              </button>
              {away.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={chipButton(draft.packedPlaceId === p.id)}
                  aria-pressed={draft.packedPlaceId === p.id}
                  onClick={() => setDraft((d) => ({ ...d, packedPlaceId: p.id }))}
                >
                  {p.name}
                </button>
              ))}
            </div>
          )}
        </fieldset>
      ) : null}

      <div className="flex gap-2">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" onClick={save} disabled={costInvalid}>
          Done
        </Button>
      </div>
    </Dialog>
  );
}
